import { Component, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { DateTime as LuxonDateTime } from 'luxon';
import { FormatDateTimePipe } from 'src/shared/pipes/format-date-time.pipe';
import { ActivatedRoute, Router } from '@angular/router';

import { Communication } from 'src/providers/communication/communication';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { PrivateClient } from 'src/providers/private-client/private-client';
import { TimeoutErrorHandler } from 'src/shared/providers/http/timeout-error-handler';

import { InAppBrowserUtils } from 'src/shared/utils/in-app-browser-utils';

import { Loading } from 'src/shared/components/loading/loading';
import { Timeout } from 'src/shared/components/timeout/timeout';

declare const window: any;

@Component({
  selector: 'client-messages',
  templateUrl: './client-messages.html'
})

export class ClientMessagesPage extends Timeout implements OnDestroy {

  backPage: string;
  client: any;
  errorMessage: string;
  form: UntypedFormGroup;
  messages: any;
  select: string;
  submitted: boolean;
  displayName: string;
  phoneNumber: string;
  clientId: any;
  chatRoomKey: string;
  fromNotification: boolean;

  constructor(
    public communication: Communication,
    private fb: UntypedFormBuilder,
    private iabUtils: InAppBrowserUtils,
    private navCtrl: CustomNavController,
    public privateClient: PrivateClient,
    private router: Router,
    public route: ActivatedRoute,
    timeoutErrorHandler: TimeoutErrorHandler
  ) {
    super(timeoutErrorHandler, router, route);
    this.form = this.fb.group({
      message: ['', Validators.required],
    });
  }

  @Loading()
  async ionViewDidEnter() {
    try {
      this.loaded = false;
      this.clientId = this.route.snapshot.paramMap.get('clientId');
      this.phoneNumber = this.navCtrl.getParam('phoneNumber');
      this.displayName = this.navCtrl.getParam('displayName');
      this.chatRoomKey = this.navCtrl.getParam('chatRoomKey');
      this.fromNotification = this.navCtrl.getParam('fromNotification') || this.clientId?.includes('homekeeper_id-');
      const isPrivate = this.navCtrl.getParam('isPrivate');
      if (isPrivate && (!this.phoneNumber || !this.displayName)) {
        const client = this.navCtrl.getParam('client') || await this.getClient();
        this.phoneNumber = client.phone_number;
        this.displayName = client.account_name || client.first_name;
      }
      this.backPage = localStorage.getItem('clientMessagesBackPage') || 'messages';
      if (this.fromNotification) {
        this.backPage = 'dashboard';
        this.messages = await this.communication.getSharedInbox(null, this.chatRoomKey);
        this.displayName = this.messages?.[0]?.from_member?.first_name || 'Notification';
      } else {
        this.messages = await this.communication.getSharedInbox(this.clientId, this.chatRoomKey);
      }
      await this.markMessagesAsRead();
      console.log(this.phoneNumber)
    } catch(err) {
      console.error(err);
      this.timeoutHandler(err);
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
    this.loaded = true;
  }

  async markMessagesAsRead() {
    let array: any = [];
    this.messages.map((message) => {
      if (!message.is_read && message.from_member.type == 'CustomerMember' && message?.chat_room_key) {
        array.push((message?.chat_room_key));
      }
    });
    if (array.length > 0) {
      await this.communication.markMessagesAsRead(array);
    }
  }

  async getClient() {
    const clientId = this.route.snapshot.paramMap.get('clientId');
    return await this.privateClient.getPrivateClientInfo(clientId);
  }

  getJobDate(date, timezone) {
    return new FormatDateTimePipe().transform(LuxonDateTime.fromISO(date).setZone(timezone).toISO(), 'LLL d');
  }

  @Loading()
  async sendMessage(attachment = []) {
    this.errorMessage = '';
    this.submitted = true;
    if (attachment.length == 0 && !this.form.get('message').valid) {
      return;
    }
    let payload: any = {
      to_customer_id: this.clientId,
      message: {
        text: this.form.value.message,
        files: attachment
      }
    };
    if (this.fromNotification) {
      delete payload.to_customer_id;
      payload.to_chat_room_key = this.chatRoomKey;
    }
    try {
      const response = await this.communication.sendSharedInboxMessage(payload);
      if (response?.data?.message == 'We couldn\'t send your message, please try again later!') {
        return this.errorMessage = response.data.message;
      }
      if (this.fromNotification) {
        this.messages = await this.communication.getSharedInbox(null, this.chatRoomKey);
      } else {
        this.messages = await this.communication.getSharedInbox(this.clientId, this.chatRoomKey);
      }
      this.form.patchValue({
        message: ''
      });
      this.submitted = false;
    } catch (err) {
      this.timeoutHandler(err);
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  async sendAttachment() {
    this.errorMessage = '';
    try {
      const attachment = await this.communication.addAttachment();
      if (attachment == '') {
        return this.errorMessage = 'Unable to attach photo. Please upload a PNG or JPEG file.';
      }
      await this.sendMessage([attachment]);
    } catch (err) {
      this.timeoutHandler(err);
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  getMessageDateTime(messageDateTime) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const relativeDate = LuxonDateTime.fromISO(messageDateTime).setZone(timezone).toRelative();
    if (relativeDate.includes('day') || relativeDate.includes('month') || relativeDate.includes('year')) {
      return (new FormatDateTimePipe().transform(LuxonDateTime.fromISO(messageDateTime).setZone(timezone).toISO(), 'EEE LLL d') + ' ' + new FormatDateTimePipe().transform(LuxonDateTime.fromISO(messageDateTime).setZone(timezone).toISO(), 'h:mma'));
    } else {
      return relativeDate;
    }
  }

  callClient() {
    this.iabUtils.openUrl(`tel://${this.phoneNumber}`);
  }

  ngOnDestroy() {
    this.errorMessage = '';
  }

}
